import {
  Avatar,
  Box,
  Collapsible,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Separator,
  Text,
  chakra,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  LuChevronDown,
  LuChevronsLeft,
  LuChevronsRight,
  LuExternalLink,
  LuLogOut,
  LuMoon,
  LuSun,
  LuUserCircle2,
} from "react-icons/lu";
import {
  Link as RouterLink,
  NavLink,
  useLocation,
} from "react-router-dom";
import { useAuth } from "@/shared/session";
import { UserAccess } from "@/core/session/components/UserAccess";
import { useGetCustomReports } from "@/core/customReports/useCases/getCustomReports";
import type { CustomReport } from "@/core/customReports/types/CustomReport";
import { getMainHostName } from "@/utils/getMainHostName";
import { ROUTES, ROUTE_SECTIONS, type Route } from "@/utils/routes";
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
} from "../chakra/menu";
import { Select } from "../selects/Select";
import { useColorMode } from "../chakra/color-mode";
import { useConfirm } from "../modals/useConfirm";
import { notify } from "../chakra/notify";

export const SIDEBAR_WIDTH = "272px";
export const SIDEBAR_COLLAPSED_WIDTH = "76px";

interface SidebarProps {
  collapsed: boolean;
  onToggle?: () => void;
}

const ChakraNavLink = chakra(NavLink);

const navItemStyles = {
  display: "flex",
  alignItems: "center",
  gap: "3",
  w: "full",
  px: "3",
  py: "2",
  rounded: "md",
  color: "fg.muted",
  fontSize: "sm",
  fontWeight: "medium",
  transition: "background 0.15s, color 0.15s",
  _hover: { bg: "bg.muted", color: "fg" },
  "&.active": {
    bg: "brand.subtle",
    color: "brand.fg",
    fontWeight: "semibold",
  },
} as const;

export const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
  const { user, session, onChangeZone, onLogout } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const confirm = useConfirm();

  const handleLogout = async () => {
    const ok = await confirm({
      title: "¿Cerrar sesión?",
      description:
        "Tendrás que volver a iniciar sesión para seguir trabajando.",
      confirmLabel: "Cerrar sesión",
      colorPalette: "red",
    });
    if (ok) onLogout?.();
  };
  const { data: customReports } = useGetCustomReports(
    session?.zone ?? "",
  );

  const currentZone = user?.zones.find(
    (zone) => zone.id === session?.zone,
  );

  const zones = useMemo(() => {
    return (
      user?.zones.sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [user?.zones]);
  return (
    <Flex
      as="nav"
      aria-label="Menú principal"
      direction="column"
      bg="bg.sidebar"
      color="fg"
      borderRightWidth="1px"
      borderColor="border"
      w={collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
      minW={collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
      h="100dvh"
      position="sticky"
      top={0}
      flexShrink={0}
      transition="width 0.2s ease, min-width 0.2s ease"
      zIndex={20}
      overflow="hidden"
    >
      <Flex
        align="center"
        justify={collapsed ? "center" : "space-between"}
        px={collapsed ? 2 : 4}
        pt={4}
        pb={3}
        gap={2}
      >
        <RouterLink to="/" aria-label="Ir al inicio">
          <HStack gap={3}>
            <Image
              src="/stocks-logo.svg"
              alt="Geslub Stocks"
              boxSize="38px"
              objectFit="contain"
              flexShrink={0}
            />
            <Heading
              as="h1"
              size="sm"
              lineHeight="1.1"
              letterSpacing="wider"
              textTransform="uppercase"
              srOnly={collapsed}
              aria-label="Geslub Stocks"
              color="brand.fg"
            >
              Stocks
              <Text
                as="span"
                display="block"
                fontSize="xs"
                fontWeight="medium"
                letterSpacing="normal"
                textTransform="none"
                color="brand.fg"
              >
                Geslub Platform
              </Text>
            </Heading>
          </HStack>
        </RouterLink>
        {!collapsed && onToggle && (
          <IconButton
            aria-label="Contraer menú"
            variant="ghost"
            size="sm"
            color="fg.muted"
            _hover={{ bg: "bg.muted", color: "fg" }}
            onClick={onToggle}
          >
            <LuChevronsLeft />
          </IconButton>
        )}
      </Flex>

      {collapsed && onToggle && (
        <Flex justify="center" pb={2}>
          <IconButton
            aria-label="Expandir menú"
            variant="ghost"
            size="sm"
            color="fg.muted"
            _hover={{ bg: "bg.muted", color: "fg" }}
            onClick={onToggle}
          >
            <LuChevronsRight />
          </IconButton>
        </Flex>
      )}

      {!collapsed && (
        <Box px={4} pb={3}>
          <Text fontSize="xs" color="fg.muted" mb={1.5}>
            Zona
          </Text>
          <Select
            inputId="zone-select"
            aria-label="Zona"
            placeholder="Selecciona zona"
            options={zones.map((zone) => ({
              value: zone.id,
              label: zone.name,
            }))}
            value={
              currentZone
                ? { value: currentZone.id, label: currentZone.name }
                : undefined
            }
            onChange={(option) => {
              if (!option || option.value === session?.zone) return;
              onChangeZone?.(option.value);
              notify.info(`Viendo zona ${option.label}`);
            }}
          />
        </Box>
      )}
      <Separator borderColor="border" />

      <Box
        flex={1}
        overflowY="auto"
        px={collapsed ? 2 : 3}
        pb={2}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(15, 23, 42, 0.14) transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(15, 23, 42, 0.14)",
            borderRadius: "9999px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            background: "rgba(15, 23, 42, 0.26)",
          },
        }}
      >
        {ROUTE_SECTIONS.map((section) => {
          const routes = ROUTES.filter(
            (route) => route.section === section,
          );
          if (!routes.length) return null;
          return (
            <Box key={section} pt={3}>
              {!collapsed && (
                <Text
                  px={3}
                  pb={1.5}
                  fontSize="2xs"
                  fontWeight="semibold"
                  letterSpacing="widest"
                  textTransform="uppercase"
                  color="fg.subtle"
                >
                  {section}
                </Text>
              )}
              <Flex direction="column" gap={0.5}>
                {routes.map((route) => (
                  <UserAccess
                    key={route.label}
                    roles={route.permissions}
                  >
                    {route.subRoutes ? (
                      <NavGroup
                        route={route}
                        collapsed={collapsed}
                        onExpandSidebar={() => onToggle?.()}
                        customReports={
                          route.label === "Reportes"
                            ? customReports
                            : undefined
                        }
                      />
                    ) : (
                      <ChakraNavLink
                        to={route.href}
                        {...navItemStyles}
                        justifyContent={
                          collapsed ? "center" : "flex-start"
                        }
                        px={collapsed ? 0 : 3}
                        title={collapsed ? route.label : undefined}
                      >
                        {route.icon && (
                          <Icon as={route.icon} boxSize={5} />
                        )}
                        {!collapsed && <span>{route.label}</span>}
                      </ChakraNavLink>
                    )}
                  </UserAccess>
                ))}
              </Flex>
            </Box>
          );
        })}
      </Box>

      <Separator borderColor="border" />
      <MenuRoot positioning={{ placement: "top-start" }}>
        <MenuTrigger asChild>
          <chakra.button
            type="button"
            display="flex"
            alignItems="center"
            gap={3}
            w="full"
            px={collapsed ? 2 : 4}
            py={3}
            textAlign="left"
            justifyContent={collapsed ? "center" : "flex-start"}
            _hover={{ bg: "bg.muted" }}
            aria-label="Menú de usuario"
          >
            <Avatar.Root
              size="sm"
              colorPalette="gray"
              variant="subtle"
              flexShrink={0}
            >
              <Avatar.Fallback
                name={[user?.name, user?.lastname]
                  .filter(Boolean)
                  .join(" ")}
              />
            </Avatar.Root>
            {!collapsed && (
              <>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" truncate>
                    {user?.name}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" truncate>
                    {currentZone?.name ?? ""}
                  </Text>
                </Box>
                <Icon as={LuChevronDown} color="fg.muted" />
              </>
            )}
          </chakra.button>
        </MenuTrigger>
        <MenuContent minW="14rem">
          <MenuItem value="account" asChild cursor="pointer">
            <a
              href={
                window.location.hostname === "localhost"
                  ? "http://localhost:3300"
                  : `https://${getMainHostName()}`
              }
            >
              <LuUserCircle2 />
              Cuenta
            </a>
          </MenuItem>
          <MenuItem
            value="color-mode"
            cursor="pointer"
            closeOnSelect={false}
            onClick={toggleColorMode}
          >
            {colorMode === "dark" ? <LuSun /> : <LuMoon />}
            {colorMode === "dark" ? "Modo claro" : "Modo oscuro"}
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            value="logout"
            cursor="pointer"
            onClick={handleLogout}
          >
            <LuLogOut />
            Cerrar sesión
          </MenuItem>
        </MenuContent>
      </MenuRoot>
    </Flex>
  );
};

interface NavGroupProps {
  route: Route;
  collapsed: boolean;
  onExpandSidebar: () => void;
  customReports?: CustomReport[];
}

const NavGroup = ({
  route,
  collapsed,
  onExpandSidebar,
  customReports,
}: NavGroupProps) => {
  const location = useLocation();
  const subRoutes = route.subRoutes ?? [];
  const isActive = subRoutes
    .flat()
    .some((subRoute) => location.pathname === subRoute.href);
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  const hasCustomReports = !!customReports?.length;

  if (collapsed) {
    return (
      <chakra.button
        type="button"
        {...navItemStyles}
        justifyContent="center"
        px={0}
        title={route.label}
        aria-label={route.label}
        onClick={() => {
          onExpandSidebar();
          setOpen(true);
        }}
        {...(isActive
          ? { bg: "brand.subtle", color: "brand.fg" }
          : {})}
      >
        {route.icon && <Icon as={route.icon} boxSize={5} />}
      </chakra.button>
    );
  }

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Collapsible.Trigger asChild>
        <chakra.button
          type="button"
          {...navItemStyles}
          {...(isActive && !open ? { color: "brand.fg" } : {})}
        >
          {route.icon && <Icon as={route.icon} boxSize={5} />}
          <Text as="span" flex={1} textAlign="left">
            {route.label}
          </Text>
          <Icon
            as={LuChevronDown}
            transition="transform 0.2s"
            transform={open ? "rotate(180deg)" : undefined}
          />
        </chakra.button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Flex direction="column" gap={0.5} pt={0.5} pb={1}>
          {subRoutes.map((group, index) => (
            <Flex key={index} direction="column" gap={0.5}>
              {index > 0 && (
                <Separator borderColor="border" mx={3} my={1} />
              )}
              {group.map((subRoute) => (
                <UserAccess
                  key={subRoute.href}
                  roles={subRoute.permissions}
                >
                  <ChakraNavLink
                    to={subRoute.href}
                    {...navItemStyles}
                    py={1.5}
                    ps={11}
                    fontWeight="normal"
                  >
                    {subRoute.label}
                  </ChakraNavLink>
                </UserAccess>
              ))}
            </Flex>
          ))}
          {hasCustomReports && (
            <>
              <Separator borderColor="border" mx={3} my={1} />
              <Text
                ps={11}
                pt={1}
                fontSize="2xs"
                fontWeight="semibold"
                letterSpacing="wider"
                textTransform="uppercase"
                color="fg.subtle"
              >
                Personalizados
              </Text>
              {customReports!.map((report) => {
                if (report.type === "external_link") {
                  return (
                    <chakra.a
                      key={report.id}
                      href={report.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...navItemStyles}
                      py={1.5}
                      ps={11}
                      fontWeight="normal"
                    >
                      <Text as="span" flex={1}>
                        {report.name}
                      </Text>
                      <Icon as={LuExternalLink} boxSize={3.5} />
                    </chakra.a>
                  );
                }
                const enBucketPrivado =
                  report.url.startsWith("gs://");
                const destino = enBucketPrivado
                  ? `/custom-report?id=${report.id}`
                  : `/custom-report?url=${encodeURIComponent(report.url)}`;
                return (
                  <ChakraNavLink
                    key={report.id}
                    to={destino}
                    {...navItemStyles}
                    py={1.5}
                    ps={11}
                    fontWeight="normal"
                  >
                    {report.name}
                  </ChakraNavLink>
                );
              })}
            </>
          )}
        </Flex>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};
